import type { ValueFormat } from "@/types/dashboard.types";

const compact = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const comma = new Intl.NumberFormat("en-US");
// Un-tagged table cells: thousands separators + at most 2 decimals (trailing
// zeros trimmed, since there's no `minimumFractionDigits`). Turns the backend's
// raw numeric strings ("520125.000000000") into "520,125".
const cellNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
// Exact, comma-grouped currency for financial statements ($1,234,567) — no
// compaction, so P&L line items read precisely rather than as "$1.2M".
const currencyExact = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
// Exact 2-decimal currency ($118.42) for per-unit financial metrics (CLTR, CAC,
// AOV) where the cents are the point and a dropped digit misaligns the column.
// Both `minimumFractionDigits` and `maximumFractionDigits` are 2 so trailing
// zeros are KEPT ($120.00, not $120) — that's what makes a column line up.
const currency2dp = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Cached per precision — formatWithPrecision runs per heatmap cell, and
// Intl.NumberFormat construction is the expensive part.
const precisionFormats = new Map<number, Intl.NumberFormat>();
function precisionFormat(precision: number): Intl.NumberFormat {
  let nf = precisionFormats.get(precision);
  if (!nf) {
    nf = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    });
    precisionFormats.set(precision, nf);
  }
  return nf;
}

/** Round `value` to `digits` decimal places (default 1) without string round-trips. */
export function round(value: number, digits = 1): number {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}

/**
 * Coerces a cell value to a finite number, or `null` when it isn't numeric.
 * Backend query results routinely return numeric columns as strings
 * (e.g. `"3.99"`), so callers that need to shade/compare must coerce first
 * rather than gating on `typeof === "number"`.
 */
export function toNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** True for numbers and numeric strings, including comma-grouped values from SQL. */
export function looksNumeric(value: unknown): boolean {
  const raw = typeof value === "string" ? value.replace(/,/g, "") : value;
  return toNumber(raw) !== null;
}

// Tokens whose output is COMPACTED (K/M suffix) and therefore carries no fixed
// decimal contract — a `precision` override is meaningless against them and is
// ignored rather than silently de-compacting a "$202K" into "$202,450.00".
const COMPACTING_FORMATS = new Set(["currency", "currency_compact", "compact"]);

/**
 * Formats `value` with an EXPLICIT number of decimal places, keeping the affix
 * (`$` / `%`) its `format` token implies. Compacting tokens have no fixed-decimal
 * contract, so they ignore `precision` (see `COMPACTING_FORMATS`).
 */
function formatWithPrecision(value: number, format: ValueFormat | undefined, precision: number): string {
  const nf = precisionFormat(precision);
  switch (format) {
    case "currency_exact":
    case "currency_2dp":
      // Sign OUTSIDE the `$` (ASCII hyphen), matching Intl's own currency-style
      // output on the non-precision path ("-$3.20"). The U+2212 true minus is
      // exclusively formatSigned's concern.
      return `${value < 0 ? "-" : ""}$${nf.format(Math.abs(value))}`;
    case "percentage":
      return `${nf.format(value)}%`;
    default:
      return nf.format(value);
  }
}

/**
 * Formats a value for display. Strings pass through unchanged (specs may supply
 * already-formatted display values like "$2.2M" or "M3"). Null/empty -> em dash.
 *
 * `precision` (schema: `HeatmapConfig.precision`) overrides the token's own
 * decimal places; the compacting tokens ignore it (a "$202K" has no cents to
 * fix). Omit it — as every caller predating it does — to keep each token's
 * default exactly as before.
 *
 * @example formatValue(2_200_000, "currency")         // "$2.2M"
 * @example formatValue(202_450, "currency_compact")   // "$202.5K"
 * @example formatValue(118.42, "currency_2dp")        // "$118.42"
 * @example formatValue(18.42, "percentage")           // "18.4%"
 */
export function formatValue(value: unknown, format?: ValueFormat, precision?: number): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value !== "number") return String(value);

  if (precision !== undefined && !COMPACTING_FORMATS.has(format ?? "")) {
    return formatWithPrecision(value, format, precision);
  }

  switch (format) {
    // `currency` has ALWAYS compacted ($2.2M); `currency_compact` is the explicit
    // name for that behavior, so specs can say what they mean. They are deliberate
    // aliases — `currency` keeps its meaning, existing specs are untouched.
    case "currency":
    case "currency_compact":
      return `$${compact.format(value)}`;
    case "currency_exact":
      return currencyExact.format(value);
    case "currency_2dp":
      return currency2dp.format(value);
    case "compact":
      return compact.format(value);
    case "percentage":
      return `${round(value, 1)}%`;
    case "number":
      return comma.format(value);
    default:
      return comma.format(value);
  }
}

/**
 * Formats a table cell that carries NO explicit `format` directive. Decides by
 * VALUE TYPE, never by column name (a hard rule in this repo — specs describe
 * many companies' schemas generically): any value that coerces to a finite
 * number — including the numeric STRINGS the backend routinely returns for
 * numeric columns (e.g. `"520125.000000000"`) — gets thousands separators and at
 * most 2 decimal places (trailing zeros trimmed). Booleans render Yes/No.
 * Genuinely non-numeric text (labels, dates, ids that aren't purely numeric)
 * passes through unchanged. Columns with a `format`/`cellType` in the spec never
 * reach here — they go through {@link formatValue} and keep their currency /
 * percent / entity handling.
 *
 * Numeric STRINGS beyond `Number.MAX_SAFE_INTEGER` (e.g. a 19-digit id string)
 * would lose their trailing digits through `Number()` coercion, so they pass
 * through VERBATIM rather than being reformatted into a corrupted value.
 *
 * @example formatCellValue("520125.000000000")        // "520,125"
 * @example formatCellValue(3.14159)                   // "3.14"
 * @example formatCellValue("North America")           // "North America"
 * @example formatCellValue("12345678901234567890")    // "12345678901234567890"
 */
export function formatCellValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const n = toNumber(value);
  if (n !== null) {
    // A numeric string past the IEEE-754 safe-integer range has already lost
    // precision as `n`; formatting it would render corrupted digits. Return the
    // caller's original string so big ids survive intact.
    if (typeof value === "string" && Math.abs(n) > Number.MAX_SAFE_INTEGER) {
      return value;
    }
    return cellNumber.format(n);
  }
  return String(value);
}

/**
 * Formats a signed delta with a leading sign (e.g. "+18.4%", "-8.7%").
 *
 * @example formatDelta(18.4)            // "+18.4%"
 * @example formatDelta(-1200, "number") // "-1,200"
 */
export function formatDelta(value: number, format?: ValueFormat): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatValue(value, format ?? "percentage")}`;
}

/**
 * Formats a value with an explicit sign for display in a signed cell (schema:
 * `HeatmapConfig.signed`): `+` for positives, a TRUE minus sign (U+2212, not the
 * ASCII hyphen) for negatives, and no sign at all for zero. The magnitude is
 * formatted through {@link formatValue} (so the `valueFormat` token's `$`/`%`
 * still applies) — the sign is prepended to the magnitude rather than left to
 * `Intl`, which would emit "-$3.20" with a hyphen and the sign OUTSIDE the affix.
 *
 * Distinct from {@link formatDelta}, which is the KPI delta's ASCII-hyphen
 * convention and is unchanged.
 *
 * @example formatSigned(5, "currency_2dp")       // "+$5.00"
 * @example formatSigned(-3.2, "currency_2dp")    // "−$3.20"
 * @example formatSigned(0, "currency_2dp")       // "$0.00"
 */
export function formatSigned(value: number, format?: ValueFormat, precision?: number): string {
  const magnitude = formatValue(Math.abs(value), format, precision);
  if (value > 0) return `+${magnitude}`;
  if (value < 0) return `−${magnitude}`;
  return magnitude;
}
